export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export function scrollAngle(top, height, viewport, header = 0) {
  const distance = Math.max(1, viewport + height - header - 48);
  return clamp((viewport - 24 - top) / distance, 0, 1) * 180;
}

export function angleDistance(current, target, circular = false) {
  return circular ? ((target - current + 540) % 360 + 360) % 360 - 180 : target - current;
}

export function easeAngle(current, target, elapsed, immediate = false, circular = false) {
  const delta = angleDistance(current, target, circular);
  if (immediate || Math.abs(delta) < 0.025) return target;
  const next = current + delta * (1 - Math.exp(-Math.min(elapsed, 64) / 75));
  return circular ? ((next % 360) + 360) % 360 : next;
}

export function framePosition(angle, mode) {
  const count = mode === 'full' ? 96 : 95;
  const value = mode === 'full' ? ((angle % 360) + 360) % 360 / 360 * count : clamp(angle, 0, 180) / 180 * (count - 1);
  const snapped = Math.abs(value - Math.round(value)) < 0.0000001 ? Math.round(value) : value;
  const exact = mode === 'full' ? snapped % count : snapped;
  const first = Math.floor(exact);
  return {first, second: mode === 'full' ? (first + 1) % count : Math.min(first + 1, count - 1), mix: exact - first, count};
}

const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const saveData = Boolean(navigator.connection?.saveData);

export class RingViewer {
  constructor(section) {
    this.section = section;
    this.config = JSON.parse(section.querySelector('[data-ring-config]').textContent);
    this.copy = this.config.copy;
    this.lifecycle = new AbortController();
    this.destroyed = false;
    this.clip = section.dataset.ringViewer;
    this.image = section.querySelector('[data-ring-still]');
    this.frame = section.querySelector('.spin-frame');
    this.range = section.querySelector('[data-ring-range]');
    this.readout = section.querySelector('[data-ring-value]');
    this.fill = section.querySelector('.spin-progress-fill');
    this.enable = section.querySelector('[data-ring-load]');
    this.status = section.querySelector('[data-ring-status]');
    this.toggle = section.querySelector('[data-ring-full]');
    this.panel = section.querySelector('[data-ring-panel]');
    this.front = section.querySelector('[data-ring-front]');
    this.retry = section.querySelector('[data-ring-retry]');
    this.label = section.querySelector('[data-ring-label]');
    this.mode = 'half';
    this.target = this.angle = 0;
    this.records = new Map();
    this.failed = new Set();
    this.candidateAngle = null;
    this.visible = false;
    this.pending = 0;
    this.near = false;
    this.optedIn = false;
    this.raf = 0;
    this.lastTime = 0;
    this.lastPaint = '';
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = 768;
    this.canvas.hidden = true;
    this.canvas.setAttribute('role', 'img');
    this.canvas.setAttribute('aria-label', this.image.alt);
    this.context = this.canvas.getContext('2d', {alpha: false});
    if (this.context) this.frame.append(this.canvas);
    this.image.draggable = false;
    this.bind();
    this.instructions();
    this.controls();
    section.querySelector('[data-ring-controls]').hidden = false;
  }
  automatic() { return this.mode === 'half' && (this.optedIn || (!reduced.matches && !saveData)); }
  instructions() {
    this.enable.hidden = this.automatic() || this.mode === 'full';
    this.enable.textContent = this.copy.enable_scroll;
    this.status.textContent = this.mode === 'full'
      ? this.copy.status_full
      : this.automatic() ? this.copy.status_scroll
        : this.copy.status_static;
  }
  controls() {
    const degree = Math.round(this.target);
    this.range.max = this.mode === 'full' ? '360' : '180';
    this.range.value = String(degree);
    this.label.textContent = this.mode === 'full' ? this.copy.full_label : this.copy.half_label;
    this.readout.textContent = degree === 0 || degree === 360 ? this.copy.right : degree === 90 ? this.copy.front : degree === 180 ? this.copy.left : degree === 270 ? this.copy.rear : degree + '°';
    this.range.setAttribute('aria-valuetext', this.readout.textContent + ', ' + this.copy.degree.replace('[angle]', String(degree)));
    this.fill.style.transform = 'scaleX(' + degree / Number(this.range.max) + ')';
  }
  url(mode, index) {
    return this.config.frames[mode][index];
  }
  positionReady(angle) {
    const position = framePosition(angle,this.mode);
    const first = this.records.get(this.mode + ':' + position.first);
    const second = this.records.get(this.mode + ':' + position.second);
    return first?.state === 'ready' && (position.mix < 0.001 || position.first === position.second || second?.state === 'ready');
  }
  candidate() {
    if (this.candidateAngle === null) this.candidateAngle = easeAngle(this.angle,this.target,16,reduced.matches,this.mode === 'full');
    const position = framePosition(this.candidateAngle,this.mode);
    if (this.failed.has(this.mode + ':' + position.first) || position.mix >= 0.001 && this.failed.has(this.mode + ':' + position.second)) this.candidateAngle = this.target;
    const distance = angleDistance(this.angle,this.target,this.mode === 'full');
    if (Math.abs(distance) >= 0.025) {
      if (this.positionReady(this.target)) this.candidateAngle = this.target;
      else {
        let progress = this.positionReady(this.candidateAngle) ? Math.abs(angleDistance(this.angle,this.candidateAngle,this.mode === 'full')) : 0;
        for (const record of this.records.values()) {
          if (record.mode !== this.mode || record.state !== 'ready') continue;
          const angle = record.index * (this.mode === 'full' ? 360 / 96 : 180 / 94);
          const step = angleDistance(this.angle,angle,this.mode === 'full');
          if (step * distance > 0 && Math.abs(step) <= Math.abs(distance) && Math.abs(step) > progress) {
            this.candidateAngle = angle;
            progress = Math.abs(step);
          }
        }
      }
    }
    return this.candidateAngle;
  }
  request(mode,index) {
    const key = mode + ':' + index;
    if (this.destroyed || this.records.has(key) || this.failed.has(key) || this.pending >= 4) return;
    const image = new Image();
    const record = {image,index,mode,state:'loading',cancel:null};
    this.records.set(key,record);
    this.pending++;
    image.decoding = 'async';
    const finish = state => {
      if (record.state !== 'loading') return;
      clearTimeout(timer);
      image.onload = image.onerror = null;
      this.pending--;
      record.state = state;
      if (this.destroyed) return;
      if (state === 'error') this.failed.add(key);
      this.prune();
      if (this.mode === mode && this.near) { this.warm(); this.start(); }
    };
    const timer = setTimeout(() => { finish('error'); image.src = ''; },6000);
    record.cancel = () => {
      if (record.state !== 'loading') return;
      clearTimeout(timer);
      image.onload = image.onerror = null;
      this.pending--;
      record.state = 'cancelled';
      this.records.delete(key);
      image.src = '';
    };
    image.onload = async () => {
      try { await image.decode?.(); } catch (_) {}
      if (record.state === 'loading') finish(image.naturalWidth > 0 ? 'ready' : 'error');
    };
    image.onerror = () => finish('error');
    image.src = this.url(mode,index);
  }
  warm() {
    if (!this.near || document.hidden || this.mode === 'half' && !this.automatic()) return;
    const angle = this.visible ? this.candidate() : this.target;
    const position = framePosition(angle,this.mode);
    const target = framePosition(this.target,this.mode);
    const order = this.visible ? [position.first] : [position.first,position.second];
    if (this.visible) {
      if (position.mix >= 0.001) order.push(position.second);
      order.push(target.first,target.second,position.first + 1,position.first - 1);
    } else {
      for (const record of this.records.values()) {
        if (record.mode !== this.mode || !order.includes(record.index)) record.cancel?.();
      }
    }
    for (const raw of order) {
      const index = this.mode === 'full' ? (raw + position.count) % position.count : raw;
      if (index >= 0 && index < position.count) this.request(this.mode,index);
    }
    if (!this.visible || this.positionReady(angle)) return;
    const failed = this.failed.has(this.mode + ':' + position.first) || position.mix >= 0.001 && this.failed.has(this.mode + ':' + position.second);
    this.section.setAttribute('aria-busy',String(!failed));
    this.retry.hidden = !failed;
    this.status.textContent = failed ? this.copy.loading_error : this.copy.loading_frame;
  }
  prune() {
    const desired = framePosition(this.target,this.mode).first;
    const shown = framePosition(this.visible ? this.candidateAngle ?? this.angle : this.target,this.mode);
    const count = this.mode === 'full' ? 96 : 95;
    const distance = record => {
      if (record.mode !== this.mode) return 1000;
      if (record.index === shown.first || record.index === shown.second) return -1;
      const raw = Math.abs(record.index - desired);
      return this.mode === 'full' ? Math.min(raw,count - raw) : raw;
    };
    const limit = document.hidden || !this.near && !this.visible ? 0 : this.visible ? 12 : 2;
    const candidates = [...this.records.entries()].filter(([,record]) => record.state !== 'loading').sort((a,b) => distance(b[1]) - distance(a[1]));
    for (const [key,record] of candidates) {
      if (this.records.size <= limit) break;
      record.image.onload = record.image.onerror = null;
      record.image.src = '';
      this.records.delete(key);
    }
  }
  paint(angle = this.angle) {
    if (!this.positionReady(angle)) return false;
    const position = framePosition(angle,this.mode);
    const first = this.records.get(this.mode + ':' + position.first);
    const second = this.records.get(this.mode + ':' + position.second);
    const signature = this.mode + ':' + position.first + ':' + position.second + ':' + position.mix.toFixed(3);
    if (signature !== this.lastPaint) {
      if (this.context) {
        this.context.globalAlpha = 1;
        this.context.drawImage(first.image,0,0,768,768);
        if (position.mix >= 0.001 && second?.state === 'ready') {
          this.context.globalAlpha = position.mix;
          this.context.drawImage(second.image,0,0,768,768);
          this.context.globalAlpha = 1;
        }
        this.canvas.hidden = false;
        this.image.hidden = true;
        this.canvas.setAttribute('aria-label',this.copy.rotation + ', ' + this.copy.degree.replace('[angle]',String(Math.round(angle))));
      } else {
        this.image.src = first.image.src;
        this.image.hidden = false;
      }
      this.lastPaint = signature;
    }
    this.section.removeAttribute('aria-busy');
    this.retry.hidden = true;
    this.instructions();
    return true;
  }
  start() {
    if (this.destroyed || this.raf || !this.visible || document.hidden) return;
    this.raf = requestAnimationFrame(time => this.tick(time));
  }
  tick(time) {
    this.raf = 0;
    if (this.destroyed || !this.visible || document.hidden) return;
    const elapsed = this.lastTime ? time - this.lastTime : 16;
    this.lastTime = time;
    if (this.candidateAngle === null) this.candidateAngle = easeAngle(this.angle,this.target,elapsed,reduced.matches,this.mode === 'full');
    const candidate = this.candidate();
    this.warm();
    if (!this.paint(candidate)) return;
    this.angle = candidate;
    this.candidateAngle = null;
    const moving = Math.abs(angleDistance(this.angle,this.target,this.mode === 'full')) >= 0.025;
    if (moving) this.raf = requestAnimationFrame(next => this.tick(next));
    else { this.angle = this.target; this.warm(); }
  }
  setTarget(angle, load = true) {
    this.target = clamp(angle, 0, this.mode === 'full' ? 360 : 180);
    if (this.candidateAngle !== null) {
      const candidateDistance = angleDistance(this.angle,this.candidateAngle,this.mode === 'full');
      const targetDistance = angleDistance(this.angle,this.target,this.mode === 'full');
      if (candidateDistance * targetDistance < 0 || Math.abs(candidateDistance) > Math.abs(targetDistance) || Math.abs(candidateDistance) < 0.025 && Math.abs(targetDistance) >= 0.025) this.candidateAngle = null;
    }
    this.controls();
    if (load) this.warm();
    this.start();
  }
  measure() {
    const rect = this.frame.getBoundingClientRect();
    const viewport = innerHeight || document.documentElement.clientHeight;
    this.near = rect.top < viewport + 600 && rect.bottom > -200;
    this.visible = rect.top < viewport && rect.bottom > 0;
    if (!this.near || document.hidden) {
      for (const record of this.records.values()) record.cancel?.();
    }
    if (!this.visible || document.hidden) {
      cancelAnimationFrame(this.raf); this.raf = 0; this.lastTime = 0;
      this.prune();
    }
    if (this.automatic() && this.near) this.setTarget(scrollAngle(rect.top,rect.height,viewport));
    else if (this.visible) { this.warm(); this.start(); }
  }
  full() {
    this.cancelDrag?.();
    this.mode = this.mode === 'half' ? 'full' : 'half';
    this.candidateAngle = null;
    for (const record of this.records.values()) if (record.mode !== this.mode) record.cancel?.();
    this.section.dataset.viewerMode = this.mode === 'full' ? 'manual' : 'scroll';
    this.toggle.setAttribute('aria-pressed', String(this.mode === 'full'));
    this.toggle.setAttribute('aria-expanded', String(this.mode === 'full'));
    this.panel.hidden = this.mode !== 'full';
    this.toggle.textContent = this.mode === 'full' ? this.copy.half : this.copy.full;
    this.front.hidden = this.mode !== 'full';
    this.frame.tabIndex = this.mode === 'full' ? 0 : -1;
    if (this.mode === 'full') {
      this.frame.setAttribute('role', 'group');
      this.frame.setAttribute('aria-label', this.copy.manual_aria);
    } else {
      this.frame.removeAttribute('role');
      this.frame.removeAttribute('aria-label');
    }
    this.near = true;
    this.angle = this.mode === 'half' ? clamp((this.angle % 360 + 360) % 360,0,180) : this.angle;
    this.target = this.angle;
    this.lastPaint = '';
    this.retry.hidden = true;
    this.instructions();
    this.setTarget(this.target);
    this.measure();
    if (this.mode === 'full') this.range.focus({preventScroll: true});
  }
  listen(target, type, handler, options = {}) {
    target.addEventListener(type, handler, {...options, signal: this.lifecycle.signal});
  }
  destroy() {
    this.destroyed = true;
    this.cancelDrag?.();
    this.lifecycle.abort();
    cancelAnimationFrame(this.raf);
    for (const record of this.records.values()) { record.cancel?.(); record.image.onload = record.image.onerror = null; record.image.src = ''; }
    this.records.clear();
    this.canvas.remove();
    this.image.hidden = false;
  }
  bind() {
    this.listen(this.range, 'input', () => { this.near = true; this.setTarget(Number(this.range.value)); });
    this.listen(this.enable, 'click', () => { this.optedIn = true; this.near = true; this.instructions(); this.measure(); });
    this.listen(this.toggle, 'click', () => this.full());
    this.listen(this.front, 'click', () => this.setTarget(90));
    this.listen(this.retry, 'click', () => {
      this.failed.clear();
      for (const [key,record] of this.records) if (record.state === 'error') this.records.delete(key);
      this.candidateAngle = null;
      this.retry.hidden = true;
      this.instructions();
      this.near = true;
      this.warm();
    });
    let drag = null;
    this.listen(this.frame, 'pointerdown', event => {
      if (this.mode !== 'full' || event.button > 0 || event.isPrimary === false) return;
      drag = {id: event.pointerId, x: event.clientX, y: event.clientY, angle: this.target, captured: false};
    });
    this.listen(this.frame, 'pointermove', event => {
      if (this.mode !== 'full' || !drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.captured) {
        if (Math.abs(dy) > 6 && Math.abs(dy) > Math.abs(dx)) { drag = null; return; }
        if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) return;
        drag.captured = true;
        this.frame.setPointerCapture?.(event.pointerId);
        this.frame.classList.add('is-dragging');
      }
      this.setTarget(((drag.angle + dx / Math.max(1, this.frame.clientWidth) * 360) % 360 + 360) % 360);
    });
    this.cancelDrag = () => {
      if (!drag) return;
      const pointerId = drag.id;
      drag = null;
      this.frame.classList.remove('is-dragging');
      if (this.frame.hasPointerCapture?.(pointerId)) this.frame.releasePointerCapture(pointerId);
    };
    const release = event => { if (drag?.id === event.pointerId) this.cancelDrag(); };
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) this.listen(this.frame, type, release);
    for (const type of ['pointerup', 'pointercancel']) this.listen(window, type, release, {passive: true});
    this.listen(this.frame, 'keydown', event => {
      if (this.mode !== 'full') return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const next = this.target + (event.key === 'ArrowRight' ? 1 : -1) * (event.shiftKey ? 15 : 5);
        this.setTarget((next + 360) % 360);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault(); this.setTarget(event.key === 'Home' ? 0 : 360);
      }
    });
    this.listen(this.section, 'keydown', event => {
      if (this.mode === 'full' && event.key === 'Escape') { this.full(); this.toggle.focus({preventScroll: true}); }
    });
  }
}

const viewers = new Map();
let measurement = 0;
const schedule = () => {
  if (measurement) return;
  measurement = requestAnimationFrame(() => { measurement = 0; viewers.forEach(viewer => viewer.measure()); });
};
function mount(root = document) {
  const sections = [...root.querySelectorAll('[data-lh-ring-study]')];
  if (root.matches?.('[data-lh-ring-study]')) sections.unshift(root);
  for (const section of sections) {
    if (!viewers.has(section)) viewers.set(section, new RingViewer(section));
  }
  for (const [section, viewer] of viewers) {
    if (viewer.clip !== 'emerald') continue;
    const next = [...viewers.keys()].find(candidate => candidate.dataset.ringViewer === 'marquise' && (section.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING));
    const link = section.querySelector('[data-ring-next]');
    if (next && link) { link.href = '#' + next.id; link.textContent = viewer.copy.next_link; }
  }
  schedule();
}
addEventListener('scroll', schedule, {passive: true, capture: true});
addEventListener('resize', schedule, {passive: true});
addEventListener('pageshow', schedule);
document.addEventListener('visibilitychange',schedule);
reduced.addEventListener?.('change', () => { viewers.forEach(viewer => viewer.instructions()); schedule(); });
document.addEventListener('shopify:section:load', event => mount(event.target));
document.addEventListener('shopify:section:unload', event => {
  for (const [section, viewer] of viewers) {
    if (event.target === section || event.target.contains(section)) { viewer.destroy(); viewers.delete(section); }
  }
});
mount();



